// hooks/usePerspectives.js
import { useState, useCallback } from 'react';
import { fetchPerspectives, unwrapPerspectives } from '../api/perspectivesApi';
import { getIncidentApiToken } from '../api/tokenService';

/**
 * Hook that manages the lifecycle of generating the three legal
 * perspectives (defense / prosecutor / judge) for a case.
 */
export const usePerspectives = (apiUrl) => {
  const [perspectives, setPerspectives] = useState(null);
  const [jurisdiction, setJurisdiction] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generate = useCallback(
    async (caseData) => {
      setLoading(true);
      setError(null);
      try {
        const idToken = await getIncidentApiToken();
        const response = await fetchPerspectives(caseData, apiUrl, idToken);
        const unwrapped = unwrapPerspectives(response);
        if (!unwrapped) {
          throw new Error('No perspectives returned by the server.');
        }
        setPerspectives(unwrapped.perspectives);
        setJurisdiction(unwrapped.jurisdiction || caseData?.jurisdiction || null);
        setMetadata(unwrapped.metadata);
        return unwrapped;
      } catch (err) {
        setError(err.message || 'Failed to generate perspectives.');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [apiUrl]
  );

  const reset = useCallback(() => {
    setPerspectives(null);
    setJurisdiction(null);
    setMetadata(null);
    setError(null);
  }, []);

  return { perspectives, jurisdiction, metadata, loading, error, generate, reset };
};
